/**
 * @fileoverview Role-Based Access Control (RBAC) Permissions Utility
 * Provides helper functions for managing user roles and permissions across the application.
 * Updated: All roles now have access to Task Templates
 */

// Role constants
export const ROLES = {
  ADMIN: 'admin',
  EDITOR: 'editor',
  CSM: 'csm',
  SALES: 'sales',
  MANAGER: 'manager',
  CONTRACTOR: 'contractor'
};

// Page/Feature constants
export const FEATURES = {
  DASHBOARD: 'dashboard',
  CLIENTS: 'clients',
  UPLOADS: 'uploads',
  TASKS: 'tasks',
  EMPLOYEES: 'employees',
  ANALYTICS: 'analytics',
  MASS_MESSAGING: 'mass_messaging',
  TIKTOK_VIEWER: 'tiktok_viewer',
  SURVEY: 'survey',
  TASK_TEMPLATES: 'task_templates',
  RFP_MANAGER: 'rfp_manager',
  NOTIFICATIONS: 'notifications',
  AUTOMATIONS: 'automations',
  ROLE_PERMISSIONS_MANAGER: 'role_permissions_manager',
  CONTRACTS: 'contracts',
  SPLITS: 'splits',  // Contract split percentages
  // Admin-only sheets
  PRE_LEADS: 'pre_leads',
  ONBOARDING: 'onboarding',
  SYSTEMS: 'systems',
  TALENT_PAYOUTS: 'talent_payouts',
  EMPLOYEES_SHEET: 'employees_sheet'
};

// Permission mappings
const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: {
    features: Object.values(FEATURES), // Admin has access to everything
    canViewFinancials: true,
    filterByAssignedClients: false,
    canManageRoles: true
  },
  [ROLES.EDITOR]: {
    features: [
      FEATURES.UPLOADS,
      FEATURES.TIKTOK_VIEWER,
      FEATURES.TASKS,
      FEATURES.TASK_TEMPLATES,
      FEATURES.NOTIFICATIONS
    ],
    canViewFinancials: false,
    filterByAssignedClients: false,
    canManageRoles: false
  },
  [ROLES.CSM]: {
    features: [
      FEATURES.DASHBOARD,
      FEATURES.CLIENTS,
      FEATURES.ANALYTICS,
      FEATURES.MASS_MESSAGING,
      FEATURES.TASKS,
      FEATURES.TASK_TEMPLATES,
      FEATURES.NOTIFICATIONS
    ],
    canViewFinancials: false,
    filterByAssignedClients: true,
    canManageRoles: false
  },
  [ROLES.SALES]: {
    features: [
      FEATURES.CLIENTS,
      FEATURES.TASKS,
      FEATURES.TASK_TEMPLATES,
      FEATURES.NOTIFICATIONS
    ],
    canViewFinancials: false,
    filterByAssignedClients: true,
    filterUnsignedOnly: false,  // Now controlled by database permissions
    canManageRoles: false
  },
  [ROLES.MANAGER]: {
    features: [
      FEATURES.DASHBOARD,
      FEATURES.CLIENTS,
      FEATURES.UPLOADS,
      FEATURES.TASKS,
      FEATURES.TASK_TEMPLATES,
      FEATURES.EMPLOYEES,
      FEATURES.ANALYTICS,
      FEATURES.MASS_MESSAGING,
      FEATURES.TIKTOK_VIEWER,
      FEATURES.SURVEY,
      FEATURES.RFP_MANAGER,
      FEATURES.NOTIFICATIONS,
      FEATURES.AUTOMATIONS
    ],
    canViewFinancials: false,
    filterByAssignedClients: false,
    canManageRoles: false
  },
  [ROLES.CONTRACTOR]: {
    features: [
      FEATURES.CLIENTS,
      FEATURES.TASKS,
      FEATURES.TASK_TEMPLATES,
      FEATURES.NOTIFICATIONS
    ],
    canViewFinancials: false,
    filterByAssignedClients: true,
    filterUnsignedOnly: false,
    canManageRoles: false
  }
};

/**
 * Check if a user role can access a specific feature/page
 * @param {string} role - User role
 * @param {string} feature - Feature/page identifier
 * @returns {boolean} - True if user has access
 */
export const canAccessFeature = (role, feature) => {
  if (!role || !feature) {
    return false;
  }
  
  // Try to use dynamic permissions first (loaded from API)
  const dynamicPermissions = window.__DYNAMIC_PERMISSIONS__;
  if (dynamicPermissions && dynamicPermissions[role]) {
    return dynamicPermissions[role].features.includes(feature);
  }
  
  // Fallback to static permissions
  const permissions = ROLE_PERMISSIONS[role];
  if (!permissions) {
    return false;
  }
  
  return permissions.features.includes(feature);
};

/**
 * Check if a user role can view financial information
 * @param {string} role - User role
 * @returns {boolean} - True if user can view financials
 */
export const canViewFinancials = (role) => {
  if (!role) return false;
  
  const permissions = ROLE_PERMISSIONS[role];
  if (!permissions) return false;
  
  return permissions.canViewFinancials;
};

/**
 * Check if user should see only their assigned clients
 * @param {string} role - User role
 * @returns {boolean} - True if filtering by assigned clients is required
 */
export const shouldFilterByAssignedClients = (role) => {
  if (!role) return false;
  
  const permissions = ROLE_PERMISSIONS[role];
  if (!permissions) return false;
  
  return permissions.filterByAssignedClients || false;
};

/**
 * Check if user should see only unsigned leads
 * @param {string} role - User role
 * @returns {boolean} - True if filtering by unsigned only
 */
export const shouldFilterUnsignedOnly = (role) => {
  if (!role) return false;
  
  const permissions = ROLE_PERMISSIONS[role];
  if (!permissions) return false;
  
  return permissions.filterUnsignedOnly || false;
};

/**
 * Check if user can manage roles
 * @param {string} role - User role
 * @returns {boolean} - True if user can manage roles
 */
export const canManageRoles = (role) => {
  if (!role) return false;
  
  const permissions = ROLE_PERMISSIONS[role];
  if (!permissions) return false;
  
  return permissions.canManageRoles || false;
};

/**
 * Get list of visible navigation items for a role
 * @param {string} role - User role
 * @returns {Array<string>} - List of visible navigation items
 */
export const getVisibleNavItems = (role) => {
  if (!role) return [];
  
  const permissions = ROLE_PERMISSIONS[role];
  if (!permissions) return [];
  
  return permissions.features;
};

/**
 * Get user-friendly role name
 * @param {string} role - Role identifier
 * @returns {string} - Display name for role
 */
export const getRoleDisplayName = (role) => {
  const roleNames = {
    [ROLES.ADMIN]: 'Administrator',
    [ROLES.EDITOR]: 'Editor',
    [ROLES.CSM]: 'Client Success Manager',
    [ROLES.SALES]: 'Sales',
    [ROLES.MANAGER]: 'Manager',
    [ROLES.CONTRACTOR]: 'Contractor'
  };
  
  return roleNames[role] || role;
};

/**
 * Get role description
 * @param {string} role - Role identifier
 * @returns {string} - Description of role permissions
 */
export const getRoleDescription = (role) => {
  const descriptions = {
    [ROLES.ADMIN]: 'Full access to all features and financial information',
    [ROLES.EDITOR]: 'Access to upload management and TikTok viewer only',
    [ROLES.CSM]: 'Access to assigned clients, no financial information',
    [ROLES.SALES]: 'Access to unsigned leads only, no content or financial data',
    [ROLES.MANAGER]: 'Full access except financial information',
    [ROLES.CONTRACTOR]: 'Access to assigned clients across multiple organizations'
  };
  
  return descriptions[role] || '';
};

/**
 * Get color code for role badge
 * @param {string} role - Role identifier
 * @returns {string} - CSS color class or hex code
 */
export const getRoleColor = (role) => {
  const colors = {
    [ROLES.ADMIN]: '#dc2626',
    [ROLES.EDITOR]: '#2563eb',
    [ROLES.CSM]: '#059669',
    [ROLES.SALES]: '#d97706',
    [ROLES.MANAGER]: '#7c3aed',
    [ROLES.CONTRACTOR]: '#0891b2'
  };
  
  return colors[role] || '#6b7280';
};

/**
 * Filter client data based on user role and assignments
 * @param {Array} clients - Array of client objects
 * @param {string} role - User role
 * @param {string} userId - User ID for filtering assignments
 * @returns {Array} - Filtered client array
 */
export const filterClientsByRole = (clients, role, userId) => {
  if (!clients || !Array.isArray(clients)) return [];
  if (!role) return clients;
  
  // Admin sees all clients
  if (role === ROLES.ADMIN) {
    return clients;
  }
  
  // Manager sees all clients (but no financials)
  if (role === ROLES.MANAGER) {
    return clients;
  }
  
  // Editor sees all clients (but only upload/content features)
  if (role === ROLES.EDITOR) {
    return clients;
  }
  
  // CSM and Contractor see only assigned clients
  if (role === ROLES.CSM || role === ROLES.CONTRACTOR) {
    return clients.filter(client => 
      client.assigned_employees && 
      client.assigned_employees.includes(userId)
    );
  }
  
  // Sales sees only assigned clients (backend handles unsigned filtering via permissions)
  if (role === ROLES.SALES) {
    return clients.filter(client => 
      client.assigned_employees && 
      client.assigned_employees.includes(userId)
    );
  }
  
  return clients;
};

/**
 * Remove financial fields from client data
 * @param {Object} client - Client object
 * @returns {Object} - Client object without financial fields
 */
export const stripFinancialData = (client) => {
  if (!client) return client;
  
  const {
    total_earnings,
    earnings,
    payout_email,
    payout_info,
    client_payout_percent,
    company_net_percent,
    splits,
    revenue,
    ...cleanClient
  } = client;
  
  return cleanClient;
};

export default {
  ROLES,
  FEATURES,
  canAccessFeature,
  canViewFinancials,
  shouldFilterByAssignedClients,
  shouldFilterUnsignedOnly,
  canManageRoles,
  getVisibleNavItems,
  getRoleDisplayName,
  getRoleDescription,
  getRoleColor,
  filterClientsByRole,
  stripFinancialData
};

